import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, map, Observable, tap } from 'rxjs';
import { IParams } from '../model/http';
import { ChannelType, ChannelTypeApiItem } from '../modules/crm/channel/components/channel/channel.interface';
import { activeTranslations, CHANNEL_TYPE, CHANNEL_TYPES_OBJECT } from '../enum/constants';
import { ChannelResponsibleEmployee, IChannelGroup } from '../model/channel.model';
import { formatFullName } from '../helpers';
import { ChannelTariffClass } from '../modules/crm/pages/payment/payment.infterface';

export let channelTake = localStorage?.getItem('channelTake');
export const initItemsParams: IParams = {
  sort: '',
  order: '',
  search: '',
  take: (channelTake && +channelTake) || 25,
  page: 1,
  filters: {},
};

/** Channels paid on the payment page, in their order; `free_count` are included in any tariff */
const PAID_CHANNELS: { name: string; key: CHANNEL_TYPE; free_count?: number }[] = [
  { name: 'OLX', key: CHANNEL_TYPE.OLX, free_count: 1 },
  { name: 'RIA', key: CHANNEL_TYPE.RIA },
  { name: 'Instagram', key: CHANNEL_TYPE.INSTAGRAM, free_count: 1 },
  { name: 'Facebook', key: CHANNEL_TYPE.FACEBOOK, free_count: 1 },
  { name: 'Telegram', key: CHANNEL_TYPE.TELEGRAM },
  { name: 'Telegram Bot', key: CHANNEL_TYPE.TELEGRAM_BOT, free_count: 10 },
  { name: 'Viber', key: CHANNEL_TYPE.VIBER },
  { name: 'Prom', key: CHANNEL_TYPE.PROM, free_count: 1 },
  { name: 'TikTok', key: CHANNEL_TYPE.TIKTOK, free_count: 1 },
  { name: 'WhatsApp', key: CHANNEL_TYPE.WHATSAPP },
];

export interface IItemsApiResponse {
  items?: any[];
  total?: number;
  size?: number;
  page?: number;
}

@Injectable({
  providedIn: 'root',
})
export class ChannelService {
  constructor(private readonly http: HttpClient) {}

  private readonly itemsSubject$ = new BehaviorSubject<any>({});
  public readonly items$ = this.itemsSubject$.asObservable();

  public readonly isLoader$ = new BehaviorSubject<boolean>(false);

  public queryItemsParams = { ...initItemsParams };
  public getChannelTypes(): Observable<ChannelTypeApiItem[]> {
    return this.http
      .get<{ data: ChannelTypeApiItem[] | { items?: ChannelTypeApiItem[] } }>(environment.baseURL + '/channel/types')
      .pipe(
        map((res) => {
          const data = res.data;
          return Array.isArray(data) ? data : (data?.items ?? []);
        }),
      );
  }

  /** Selectable channel types: API tells which exist, names and logos come from the frontend */
  public getChannelTypeOptions(): Observable<ChannelType[]> {
    return this.getChannelTypes().pipe(
      map((items) =>
        items
          .sort((a, b) => a.position - b.position)
          .map((item) => ({
            value: item.key,
            name: CHANNEL_TYPES_OBJECT[item.key]?.name || item.key,
            img: CHANNEL_TYPES_OBJECT[item.key]?.img_url,
            disabled: item.disabled,
          })),
      ),
    );
  }

  private getAll(params: any) {
    return this.http.post<any>(environment.baseURL + '/channel/all', params)
      .pipe(map(v => v.data));
  }

  public getCount() {
    return this.http.get<any>(environment.baseURL + '/channel/count')
      .pipe(map(res => res.data));
  }

  loadItems() {
    this.isLoader$.next(true);
    return this.getAll(this.queryItemsParams)
      .pipe(tap((items) => {
        this.itemsSubject$.next(items);
        this.isLoader$.next(false);
      }));
  }

  setQueryItemsParams(params: IParams) {
    const { sort, order, search, take, page, filters, active } = params;
    this.queryItemsParams = {
      sort: sort || typeof (sort) === 'string' ? sort : this.queryItemsParams.sort,
      order: order || typeof (order) === 'string' ? order : this.queryItemsParams.order,
      search: search || typeof (search) === 'string' ? search : this.queryItemsParams.search,
      take: take || this.queryItemsParams.take,
      page: page || this.queryItemsParams.page,
      filters: filters || this.queryItemsParams.filters,
      active: active ?? this.queryItemsParams.active ?? true,
    };
    return this.loadItems();
  }

  delete(id: string) {
    return this.http.delete(environment.baseURL + `/channel/${id}`);
  }

  update(id: string, body: any) {
    return this.http.put(environment.baseURL + `/channel/${id}`, body);
  }

  updateTake() {
    channelTake = localStorage?.getItem('channelTake');
    initItemsParams.take = (channelTake && +channelTake) || 25;
  }

  getChannelById(channel_id: string) {
    return this.http.get<any>(environment.baseURL + `/channel/${channel_id}`)
      .pipe(map(v => v.data));
  }

  destroy() {
    this.queryItemsParams = { ...initItemsParams };
  }

  archiveBulk(ids: string[]) {
    const payload = { ids };
    return this.http.post<{ data: string[] }>(environment.baseURL + '/channel/bulk/archive', payload)
      .pipe(map(v => v.data));
  }

  disconnectBulk(ids: string[]) {
    const payload = { ids };
    return this.http.post<{ data: string[] }>(environment.baseURL + '/channel/bulk/disconnect', payload)
      .pipe(map(v => v.data));
  }

  getChannelGroups() {
    return this.http.get<any>(environment.baseURL + '/channel/groups').pipe(map((v) => v.data));
  }

  saveChannelGroup(id: string | null, body: any) {
    const url = environment.baseURL + '/channel/groups' + (id ? '/' + id : '');
    return (id ? this.http.put<any>(url, body) : this.http.post<any>(url, body)).pipe(map((v) => v.data));
  }

  updateOwnChannelGroupMembership(id: string, member: boolean, distribution: boolean) {
    return this.http.put<{ data: IChannelGroup }>(environment.baseURL + '/channel/groups/' + id + '/membership', {
      member, distribution,
    }).pipe(map((response) => response.data));
  }

  deleteChannelGroup(id: string) {
    return this.http.delete<any>(environment.baseURL + '/channel/groups/' + id).pipe(map((v) => v.data));
  }

  getResponsibleEmployees(): Observable<ChannelResponsibleEmployee[]> {
    return this.http.get<{ data: Array<{
      id: string;
      first_name: string;
      last_name: string;
      email: string;
      avatar_url: string | null;
      is_active: boolean;
    }> }>(environment.baseURL + '/channel/groups/employees').pipe(
      map(({ data }) => data.map((employee) => {
        const name = formatFullName(employee.first_name, employee.last_name) || employee.email;
        return {
          id: employee.id,
          name: employee.is_active ? name : `${name} (${activeTranslations.chat_settings.inactive_employee})`,
          avatarUrl: employee.avatar_url,
          disabled: !employee.is_active,
        };
      })),
    );
  }

  getChannels(params: any = {}) {
    return this.http.get<any>(environment.baseURL + '/channel', { params })
      .pipe(map(v => v.data));
  }

  getChannelInfo(params: any = {}) {
    return this.http.get<any>(environment.baseURL + '/channel/info', { params })
      .pipe(map(v => v.data));
  }

  checkConnect(params: any = {}) {
    return this.http.get<any>(environment.baseURL + '/channel/check', { params })
      .pipe(map(v => v.data));
  }

  /**
   * Paid channels of the payment page: the price and whether it's available come from `/channel/types`,
   * the order and the free channels from the frontend. A type the API doesn't return is not shown.
   */
  getChannelTariffs(): Observable<ChannelTariffClass[]> {
    return this.getChannelTypes().pipe(
      map((types) => {
        const typesByKey = new Map(types.map((type) => [type.key, type]));
        return PAID_CHANNELS.flatMap(({ name, key, free_count }) => {
          const type = typesByKey.get(key);
          if (!type) return [];

          return [new ChannelTariffClass({ name, key, free_count, price: Number(type.unit_price) || 0, disabled: type.disabled })];
        });
      }),
    );
  }
}
